export const LONG_COVID_FAQ_HEADER = <>
  <p>The long COVID version of CoRiCal aims to help people understand their chance of feeling unwell six months after getting COVID-19 (long COVID).   </p>
</>

export const LONG_COVID_FAQ_ITEMS =  [
  {
    question: "What is long COVID and who does it affect?",
    answer: <><p>Long COVID is also known as post-COVID-19 condition or post-acute sequelae (consequences) of COVID-19. Long COVID means feeling unwell a long time after the infection (12 weeks or more). 
      This can include continued, returning or new symptoms, that are not due to any other diagnosis.</p>
      <p>These symptoms can impact everyday life and make it difficult to complete tasks, exercise, or work. While many people get better over time, some still have symptoms two years later or more. For this calculator, we show the chance of having long COVID 6 months after infection. 
        Long COVID can happen after infection with any SARS-CoV-2 strain, but this calculator focuses on the Omicron variant.</p>
      </>,
  },
  {
    question: "What sort of symptoms can long COVID cause?",
    answer: <>Long COVID can cause over 200 symptoms/conditions across many different organ systems in the body. This calculator focuses on the most common symptoms, listed below.
    <ul>
      <li><b>Cardiovascular symptoms include:</b> 
        <ul>
          <li>abnormal heart rhythm (too fast, too slow, or irregular)</li>
          <li>acute coronary (heart vessel) disease (heart attack)</li>
          <li>atrial fibrillation (irregular heart beat)</li>
          <li>chest pain</li>
          <li>heart failure</li>
          <li>hypertension (high blood pressure)</li>
          <li>myocarditis (inflammation of heart muscle)</li>
          <li>pericarditis (inflammation of tissue around the heart)</li>
        </ul>
      </li>
      <li><b>Breathing symptoms include:</b></li>
        <ul>
          <li>cough</li>
          <li>interstitial lung disease (a number of lung diseases that cause scarring (fibrosis) of the lungs. The scarring causes stiffness in the lungs which makes it difficult to breathe and get oxygen to the bloodstream)</li>
          <li>low blood oxygen</li>
          <li>pleurisy (inflammation of the lung lining) or pleural effusion (when fluid builds up in the space between the lung and the chest wall) </li>
          <li>shortness of breath</li>
        </ul>
      <li><b>Metabolic symptoms include: </b></li>
        <ul>
          <li>diabetes mellitus (high blood sugar)</li>
          <li>hyperlipidaemia (high levels of fat in the blood, including cholesterol)</li>
          <li>needing insulin</li>
          <li>obesity </li>
        </ul>
      <li><b>Neurological symptoms include: </b></li>
       <ul>
          <li>bleeding in blood vessels that supply the brain (this can cause a stroke)</li>
          <li>'brain fog' (confusion, difficulty thinking, slower response time)</li>
          <li>headache</li>
          <li>memory problems</li>
          <li>seizure</li>
          <li>smell and taste problems </li>
          <li>stroke</li>
        </ul>
      <li><b>Stomach and bowel symptoms include: </b></li>
        <ul>
          <li>constipation</li>
          <li>diarrhoea</li>
          <li>gastroesophageal reflux disease (reflux)</li>
          <li>oesophageal disorders </li>
          <li>liver disease</li>
        </ul>
    </ul> </>,
  },
  {
    question: "What other symptoms can there be?",
    answer: <>Other symptoms from long COVID are listed below. These are not currently included in the calculator. We may add them at a later stage when more data is available.
    <ul>
      <li>Blood conditions include: 
        <ul>
          <li>anaemia (not enough healthy red blood cells)</li>
          <li>deep vein thrombosis (blood clot in a deep vein, most often in the leg) </li>
          <li>pulmonary embolism (blood clot in the lung)</li>
          <li>superficial venous thrombosis (blood clot in a surface vein, most often in the leg)</li>
          <li>thromboembolism (blood clot in a vein)</li>
        </ul>
      </li>
      <li>Fatigue (feeling very tired all the time)</li>
      
      <li>Kidney conditions include: </li>
        <ul>
          <li>acute (short-term) kidney injury</li>
          <li>chronic (long-term) kidney disease</li>
        </ul>
      <li>Mental health conditions include: </li>
       <ul>
          <li>adjustment disorder</li>
          <li>anxiety</li>
          <li>depression</li>
          <li>mood disorder</li>
          <li>panic, stress, and trauma related disorders</li>
          <li>sleep disorder </li>
          <li>substance use disorders</li>
        </ul>
      <li>Musculoskeletal symptoms include: </li>
        <ul>
          <li>joint pain</li>
          <li>muscle pain</li>
          <li>muscle weakness</li>
        </ul>
      <li>Skin symptoms include:</li>
        <ul>
          <li>	hair loss</li>
          <li>	skin rash</li>
        </ul>
    </ul>
    </>,
  },
  {
    question: "Who is most at risk of getting long COVID?",
    answer: <>
      Anyone can get long COVID, even if they had no symptoms during their first week of infection. You are more likely to get long COVID if you:
      <ul>
        <li> are female</li>
        <li> have a high number of medical conditions</li>
        <li>	have a severe case of COVID-19</li>
        <li>	have not received COVID-19 vaccines</li>
        <li>	do not receive antiviral drug treatment during your first week of infection </li>
        <li>	have had COVID-19 more than once</li>
      </ul>
    </>,
  },
  {
    question: "What causes long COVID?",
    answer: <>The cause of long COVID is not fully understood. It is possible that ‘long COVID’ is not one disease, but an umbrella term for many disease processes. 
    This would explain how it can affect people so differently. Some current theories backed up by scientific evidence are listed below:
    <ul>
      <li>	COVID-19 can cause damage to your organs during the first week of infection. This damage may last after the infection has cleared.</li>
      <li>	Fragments of the virus may hide in your body after the infection has cleared. Even if these fragments can’t spread, your immune system can keep reacting to them.</li>
      <li>	COVID-19 can make your immune system go into over-drive during the first week of infection. This can cause too much inflammation throughout the body. In some people, it stays this way even after the infection has cleared.</li>
      <li>	COVID-19 may damage the lining of your blood vessels, and make your blood more likely to clot when it shouldn’t. This makes it hard for parts of your body to get enough oxygen.</li>
      <li>	There are some viruses that stay in your body in a sleeping state. An example of this is Epstein Barr Virus, which causes glandular fever. COVID-19 might re-awaken these viruses.</li>
    </ul>
    </>,
  },
  {
    question: "Is long COVID the same as other post-viral conditions? ",
    answer: <><p>The symptoms of long COVID can overlap with other conditions. </p>
    <p>Post-acute viral infection syndromes include the symptoms listed below. See <a href="https://pubmed.ncbi.nlm.nih.gov/38333536/">here</a>  for more information.</p>
    <ul>
      <li>	‘brain fog’ (difficulty thinking and focusing on a task)</li>
      <li>	disproportionate (higher than expected) levels of fatigue</li>
      <li>	flu-like symptoms</li>
      <li>	muscle and joint aches and pains</li>
      <li>	poor exercise tolerance</li>
      <li>	sensory impairment (e.g., loss of smell or taste)</li>
      <li>	unrefreshing sleep</li>
    </ul>
    <p>Chronic fatigue syndrome includes fatigue, brain fog and post-exertional malaise. See <a href="https://pubmed.ncbi.nlm.nih.gov/36412084/ ">here</a> for more information. </p>
    </>
  },

  {
    question: "Is there any way to prevent long COVID?",
    answer: <><p>The best way to prevent long COVID is to avoid getting COVID-19 in the first place. You can lower your chance of infection in several ways:</p>
    <ul>
      <li>	wear a mask</li>
      <li>	limit your time in crowds</li>
      <li>	limit your time in indoor spaces without fresh air</li>
      <li>	keep up to date with your COVID-19 vaccine boosters</li>
    </ul>
    <p>If you do get COVID-19, you can lower you chance of getting long COVID by getting antiviral drugs in your first week of infection. Ask your doctor to check if you are eligible for government funding for these drugs. Getting COVID-19 vaccines can also provide some protection against getting long COVID.  </p>
    </>
  },

  {
    question: "How do I use the calculator if I have an ongoing medical condition that isn’t listed?",
    answer: "This calculator estimates your chance of getting long COVID based on the total number of listed conditions, rather than the type of conditions. This is because there is limited data available for specific conditions. If you have a condition that is not listed, we suggest you choose another condition from the list that affects a similar body part. ",
  },

  {
    question: "How do I use the calculator if I’ve had more than 4 shots?",
    answer: "If you’ve had more than 4 shots, select the option “2 or more boosters (4 or more shots total)”. The calculator will use data focusing on 4 shots to estimate your chance of long COVID. This will be updated when more evidence is available. ",
  },

  {
    question: "How do I use the calculator if I only had 1 shot more than 3 weeks ago?",
    answer: <>We don’t currently have data on the effectiveness of 1 shot received more than 3 weeks ago. However, we know that effectiveness will decrease over time. For the closest estimate, we suggest that you select “None” in response to “How many COVID-19 vaccine doses have you had?”</>,
  },

  {
    question: "What should I do when I’m recovering from COVID-19?",
    answer: <>As soon as you know you have COVID-19, ask your doctor whether antiviral drugs are recommended for you. You should isolate from other people and ideally stay home. If you do go out, wear a P2 or N95 mask. Cloth and surgical masks are not as effective. You should avoid visiting places where there are vulnerable people, 
    such as nursing homes or hospitals (unless you need medical care yourself). See <a href="https://www.health.gov.au/topics/covid-19/testing-positive ">here</a> for more information.</>,
  },

  {
    question: "Which antiviral drugs are included in the calculator?",
    answer: <><p>The calculator considers the antiviral drugs listed below:</p>
    <ul>
      <li> Molnupiravir taken within 5 days of testing positive</li>
      <li> Metformin taken within 7 days of symptom onset at a dose of 1500mg immediate release taken over 6 days 
        (Note, this may be effective at lowering risk of long COVID, but not treating the initial infection. See <a href="https://pubmed.ncbi.nlm.nih.gov/37302406/ ">here</a> for more information.)</li>
      <li>	Metformin taken within 3 days of symptom onset at a dose of 1500mg immediate release taken over 6 days</li>
      <li>	Nirmatrelvir (Paxlovid) taken within 5 days of testing positive</li>
    </ul>
    <p>For more information on data sources, please read the document in the ‘MORE INFO’ section. For more information about antiviral drugs, eligibility criteria and cost, see the below links:  </p>
    <ul>
      <li> <a href="https://www.health.gov.au/health-alerts/covid-19/treatments">https://www.health.gov.au/health-alerts/covid-19/treatments</a></li>
      <li> <a href="https://www.health.gov.au/topics/covid-19/oral-treatments">https://www.health.gov.au/topics/covid-19/oral-treatments</a></li>
      <li> <a href="https://www.covid19treatmentguidelines.nih.gov/management/clinical-management-of-adults/nonhospitalized-adults--therapeutic-management/">https://www.covid19treatmentguidelines.nih.gov/management/clinical-management-of-adults/nonhospitalized-adults--therapeutic-management/</a></li>
    </ul>
    </>
  },

  {
    question: "Where can I learn more about long COVID?",
    answer: <><p>Some great resources are listed below:</p>
    <ul>
      <li> <a href="https://www.betterhealth.vic.gov.au/covid-19/long-covid">https://www.betterhealth.vic.gov.au/covid-19/long-covid</a></li>
      <li> <a href="https://www.unsw.edu.au/newsroom/news/2021/04/Long-COVID-what-is-it-and-what-do-we-know-about-it">https://www.unsw.edu.au/newsroom/news/2021/04/Long-COVID-what-is-it-and-what-do-we-know-about-it</a></li>
      <li> <a href="https://www.who.int/southeastasia/outbreaks-and-emergencies/covid-19/questions/post-covid-19-q-a">https://www.who.int/southeastasia/outbreaks-and-emergencies/covid-19/questions/post-covid-19-q-a</a></li>
      <li> <a href="https://hms.harvard.edu/news/most-important-question-about-long-covid">https://hms.harvard.edu/news/most-important-question-about-long-covid </a></li>
      <li> Faux, S. (2024). Long COVID: Expert advice, from diagnosis to treatment and recovery. Murdoch Books. </li>
    </ul>
    </>
  },
];
